import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Contract } from '@shared/entities/contract';
import { Maintenance } from '@shared/entities/maintenance';
import { WARRANTY_ID } from '@shared/models/constants';
import { ContractService } from '@shared/services/api/contracts.service';
import { MaintenanceService } from '@shared/services/api/maintenance.service';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { ThemeService } from '../services/theme.service';

interface AsyncResult<T> {
  data: T | null;
  failed: boolean;
}

@Component({
  selector: 'app-machine-informations',
  templateUrl: './machine-informations.component.html',
  styleUrl: './machine-informations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MachineInformationsComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _maintenanceService = inject(MaintenanceService);
  private _contractService = inject(ContractService);

  isLight = true;
  /**
   * Wrapped rather than emitting the bare value: `@if (x$ | async; as x)` treats a `null`
   * emission the same as "not yet emitted", so a request that fails (catchError mapping to
   * `null`) looked identical to one still loading — the card was stuck on its spinner forever
   * instead of showing an error. Wrapping every emission in a truthy object, settled or not,
   * lets the template distinguish "still loading" from "failed" (data is null, failed is true).
   */
  maintenance$!: Observable<AsyncResult<Maintenance>>;
  contracts$!: Observable<AsyncResult<Contract[]>>;
  warrantyContract: Contract | undefined;

  @Input() serialNumber!: string;

  get themeClass(): string {
    return this.isLight ? 'light' : 'dark';
  }

  ngOnInit() {
    this.maintenance$ = this._maintenanceService.getBySerialNumber(this.serialNumber).pipe(
      map((maintenance) => ({ data: maintenance, failed: false })),
      catchError(() => of({ data: null, failed: true }))
    );

    this.contracts$ = this._contractService.getContractsBySerialNumber(this.serialNumber).pipe(
      tap((contracts) => {
        this.warrantyContract = contracts.find((c) => c.category === WARRANTY_ID);
      }),
      map((contracts) => ({ data: contracts.filter((c) => c.category !== WARRANTY_ID), failed: false })),
      catchError(() => of({ data: null, failed: true })),
      shareReplay(1)
    );

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => (this.isLight = x))
      )
      .subscribe(() => this._cdr.markForCheck());
  }
}
